import * as BasketUpdated from '@ori-events/basket-updated';
import * as RequestShoppingContext from '@ori-events/request-shopping-context';
import * as ShoppingContextLoaded from '@ori-events/shopping-context-loaded';
import * as ToggleMenuFlyoutEvent from '@ori-events/toggle-menu-flyout';
import { EmptyBasket, AddToBag, AddToBagFilled, ShoppingBasketFilled } from '@ori-ui/icons';
import type { Theme } from '@ori-ui/mui';
import { ButtonBase, Link, styled, useMediaQuery } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useCallback, useMemo, useEffect, useState } from 'react';

import { CN_MARKET } from '../../constants';
import { useAppConfig } from '../../context';
import { useHover } from '../../hooks';
import { useTranslations } from '../../hooks/useTranslations';
import { ShoppingBagCounter } from './ShoppingBagCounter';
import { SHOPPING_BAG_ICON_ROOT } from './constants';

const ShoppingBagIconRoot = styled(Link)<{
  ownerState: { isCnMarket: boolean };
  disableRipple?: boolean;
}>(({ ownerState, theme }) => ({
  alignItems: 'center',
  color: theme.palette.grey[600],
  display: 'flex',
  fontSize: theme.typography.pxToRem(30),
  height: '100%',
  position: 'relative',

  ...(ownerState.isCnMarket && {
    color: theme.palette.error.main,
  }),

  '&:hover, &:focus, &:active': {
    color: theme.palette.grey[600],

    ...(ownerState.isCnMarket && {
      color: theme.palette.error.main,
    }),
  },
}));

export const getMinibagFlyoutEvent = (shouldOpen?: boolean) =>
  new CustomEvent(ToggleMenuFlyoutEvent.Name, {
    detail: { menuType: ToggleMenuFlyoutEvent.MenuTypes.Minibag, shouldOpen },
  });

const triggerRequestShoppingContext = () => {
  const requestShoppongContextEvent = new CustomEvent(RequestShoppingContext.Name);
  window.dispatchEvent(requestShoppongContextEvent);
};

export const ShoppingBagIcon: FC = () => {
  const { formatMessage } = useTranslations();
  const [isECatalogue, setIsECatalogue] = useState(false);
  const mobileBreakpoint = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'));
  const mobileBreakpointECatalogue = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));
  const { tenant } = useAppConfig();
  const { getTestId } = useTestId();

  const isMobile = useCallback(
    () => (isECatalogue ? mobileBreakpointECatalogue : mobileBreakpoint),
    [isECatalogue, mobileBreakpointECatalogue, mobileBreakpoint],
  );
  const ownerState = { isCnMarket: tenant === CN_MARKET };
  let iconOnHover = AddToBagFilled;
  let icon = AddToBag;

  if (tenant === CN_MARKET) {
    iconOnHover = ShoppingBasketFilled;
    icon = EmptyBasket;
  }

  const [ref, Icon] = useHover(iconOnHover, icon);

  const [shoppingBagQuantity, setShoppingBagQuantity] = useState(0);
  const [isReceivedShoppingContext, setIsReceivedShoppingContext] = useState(false);

  const updateShoppingBagQuantity = (newShoppingBagQuantity: number) => {
    if (newShoppingBagQuantity >= 0) {
      setShoppingBagQuantity(newShoppingBagQuantity);
    }
  };

  const shoppingContextLoadedEventHandler = useCallback((event: ShoppingContextLoaded.Event) => {
    updateShoppingBagQuantity(event.detail.shoppingBag.shoppingBagQuantity);
    setIsReceivedShoppingContext(true);
  }, []) as EventListener;

  const basketUpdatedEventHandler = useCallback((event: BasketUpdated.Event) => {
    updateShoppingBagQuantity(event.detail.shoppingBag.shoppingBagQuantity);
  }, []) as EventListener;

  useEffect(() => {
    setIsECatalogue(document.getElementById('catalogue-ipaper-root') !== null);
  }, []);

  useEffect(() => {
    window.addEventListener(ShoppingContextLoaded.Name, shoppingContextLoadedEventHandler);
    window.addEventListener(BasketUpdated.Name, basketUpdatedEventHandler);

    triggerRequestShoppingContext();

    return () => {
      window.removeEventListener(ShoppingContextLoaded.Name, shoppingContextLoadedEventHandler);
      window.removeEventListener(BasketUpdated.Name, basketUpdatedEventHandler);
    };
  }, [shoppingContextLoadedEventHandler, basketUpdatedEventHandler]);

  useEffect(() => {
    if (isReceivedShoppingContext) {
      window.removeEventListener(ShoppingContextLoaded.Name, shoppingContextLoadedEventHandler);
    }
  }, [isReceivedShoppingContext, shoppingContextLoadedEventHandler]);

  const onMouseEnter = useCallback(() => {
    if (isECatalogue || isMobile()) {
      return;
    }

    window.dispatchEvent(getMinibagFlyoutEvent(true));
  }, [isECatalogue, isMobile]);

  const onMouseLeave = useCallback(() => {
    if (isECatalogue || isMobile()) {
      return;
    }

    window.dispatchEvent(getMinibagFlyoutEvent(false));
  }, [isECatalogue, isMobile]);

  const onMobileEcataloguePageClick = useCallback(() => {
    window.dispatchEvent(getMinibagFlyoutEvent());
  }, []);

  const ShoppingBagContent = useMemo(
    () => (
      <>
        <Icon fontSize="inherit" />
        {Boolean(shoppingBagQuantity) && <ShoppingBagCounter shoppingBagQuantity={shoppingBagQuantity} />}
      </>
    ),
    [shoppingBagQuantity, Icon],
  );

  const formattedAriaLabel = formatMessage('shoppingBag');

  if (isMobile() && isECatalogue) {
    return (
      <ShoppingBagIconRoot
        disableRipple
        aria-label={formattedAriaLabel}
        as={ButtonBase}
        ownerState={ownerState}
        data-testid={getTestId(SHOPPING_BAG_ICON_ROOT)}
        onClick={onMobileEcataloguePageClick}
      >
        {ShoppingBagContent}
      </ShoppingBagIconRoot>
    );
  }

  return (
    <ShoppingBagIconRoot
      ref={ref}
      aria-label={formattedAriaLabel}
      ownerState={ownerState}
      href="/shopping/basket"
      data-testid={getTestId(SHOPPING_BAG_ICON_ROOT)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {ShoppingBagContent}
    </ShoppingBagIconRoot>
  );
};
